import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ModalType =
  | 'taskDetail'
  | 'taskCreate'
  | 'taskEdit'
  | 'projectCreate'
  | 'projectEdit'
  | 'leaveRequest'
  | 'leaveApproval'
  | 'timeCorrection'
  | 'missReason'
  | 'reviewFeedback'
  | 'confirmation'
  | null;

type SidebarView = 'default' | 'collapsed' | 'hidden';

const SIDEBAR_STORAGE_KEY = 'sidebarView';

const loadSidebarView = (): SidebarView => {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored === 'collapsed' || stored === 'default' || stored === 'hidden') {
      return stored;
    }
  } catch {
    // Ignore storage errors (private mode, etc.)
  }
  return 'default';
};

const persistSidebarView = (view: SidebarView) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, view);
  } catch {
    // Ignore storage errors
  }
};

export interface UIState {
  // Modal management
  activeModal: ModalType;
  modalData: Record<string, unknown> | null;
  
  // Sidebar
  sidebarView: SidebarView;
  
  // View preferences
  taskViewMode: 'list' | 'kanban' | 'calendar';
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;
  
  // Toast notifications (managed by nbiz_components, but tracking here)
  lastToast: {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    timestamp: number;
  } | null;
  
  // Mobile
  isMobileMenuOpen: boolean;
}

const initialState: UIState = {
  activeModal: null,
  modalData: null,
  sidebarView: loadSidebarView(),
  taskViewMode: 'list',
  globalLoading: false,
  loadingMessage: null,
  lastToast: null,
  isMobileMenuOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openModal: (
      state,
      action: PayloadAction<{
        type: ModalType;
        data?: Record<string, unknown>;
      }>
    ) => {
      state.activeModal = action.payload.type;
      state.modalData = action.payload.data || null;
    },
    closeModal: (state) => {
      state.activeModal = null;
      state.modalData = null;
    },
    setSidebarView: (state, action: PayloadAction<SidebarView>) => {
      state.sidebarView = action.payload;
      persistSidebarView(state.sidebarView);
    },
    toggleSidebar: (state) => {
      state.sidebarView =
        state.sidebarView === 'collapsed' ? 'default' : 'collapsed';
      persistSidebarView(state.sidebarView);
    },
    setTaskViewMode: (
      state,
      action: PayloadAction<'list' | 'kanban' | 'calendar'>
    ) => {
      state.taskViewMode = action.payload;
    },
    setGlobalLoading: (
      state,
      action: PayloadAction<{ loading: boolean; message?: string }>
    ) => {
      state.globalLoading = action.payload.loading;
      state.loadingMessage = action.payload.message || null;
    },
    showToast: (
      state,
      action: PayloadAction<{
        type: 'success' | 'error' | 'warning' | 'info';
        message: string;
      }>
    ) => {
      state.lastToast = {
        ...action.payload,
        timestamp: Date.now(),
      };
    },
    clearToast: (state) => {
      state.lastToast = null;
    },
    toggleMobileMenu: (state) => {
      state.isMobileMenuOpen = !state.isMobileMenuOpen;
    },
    closeMobileMenu: (state) => {
      state.isMobileMenuOpen = false;
    },
  },
});

export const {
  openModal,
  closeModal,
  setSidebarView,
  toggleSidebar,
  setTaskViewMode,
  setGlobalLoading,
  showToast,
  clearToast,
  toggleMobileMenu,
  closeMobileMenu,
} = uiSlice.actions;

// Selectors
export const selectActiveModal = (state: { ui: UIState }) => state.ui.activeModal;
export const selectModalData = (state: { ui: UIState }) => state.ui.modalData;
export const selectSidebarView = (state: { ui: UIState }) => state.ui.sidebarView;
export const selectTaskViewMode = (state: { ui: UIState }) => state.ui.taskViewMode;
export const selectGlobalLoading = (state: { ui: UIState }) => state.ui.globalLoading;
export const selectIsMobileMenuOpen = (state: { ui: UIState }) =>
  state.ui.isMobileMenuOpen;

export default uiSlice.reducer;
